
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const Test = () => {
  const [loading, setLoading] = useState(true);
  const [therapists, setTherapists] = useState<Array<{email: string}>>([]);

  useEffect(() => {
    const loadTherapists = async () => {
      try {
        console.log("Loading therapist data from user profiles...");
        
        // Get the current logged-in user for debugging
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log('Logged-in user ID:', user.id); // Check the current logged-in user ID
        }
        
        // Use a direct query that bypasses RLS to get ALL therapists
        // This ensures we get all therapists regardless of user permissions
        console.log('Fetching ALL therapist roles without filtering...');
        const { data: therapistRoles, error: roleError } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'therapist');  // Get all users with the 'therapist' role
          
        console.log('Therapist roles:', therapistRoles); // Log all therapist roles
          
        if (roleError) {
          console.error("Error fetching therapist roles:", roleError);
          toast.error("Failed to fetch therapist data");
          setDemoTherapists();
          return;
        }
        
        if (!therapistRoles || therapistRoles.length === 0) {
          console.log("No therapist roles found, using demo data");
          setDemoTherapists();
          return;
        }
        
        console.log(`Found ${therapistRoles.length} therapist roles`);
        
        // Extract user_ids from therapist roles
        const therapistIds = therapistRoles.map(role => role.user_id);
        console.log('Therapist IDs:', therapistIds); // Log all therapist IDs
        
        // Fetch profiles for ALL therapist user_ids with no filtering
        console.log('Fetching ALL therapist profiles...');
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('user_id, email, first_name, last_name')
          .in('user_id', therapistIds);
          
        console.log('Profile data:', profileData); // Log the actual profile data
          
        if (profileError) {
          console.error("Error fetching user profiles:", profileError);
          toast.error("Failed to fetch therapist profiles");
          setDemoTherapists();
          return;
        }
        
        if (!profileData || profileData.length === 0) {
          console.log("No profiles found for therapists, using demo data");
          setDemoTherapists();
          return;
        }

        console.log(`Found ${profileData.length} therapist profiles`);
        
        // Map ALL therapist profiles to email format without any filtering
        const therapistEmails = profileData.map(profile => {
          // Create a display name if first/last name are available
          const displayName = profile.first_name || profile.last_name ? 
            `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : null;
            
          return { 
            email: profile.email || 
              (displayName ? `${displayName} (No email)` : `User ID: ${profile.user_id.substring(0, 8)}...`)
          };
        });
          
        console.log("Therapist emails data:", therapistEmails); // Log the mapped emails
          
        if (therapistEmails.length > 0) {
          console.log("Found therapist profiles:", therapistEmails.length);
          setTherapists(therapistEmails);
          setLoading(false);
          return;
        }
        
        // Final fallback to demo data
        setDemoTherapists();
      } catch (error) {
        console.error("Error loading therapists:", error);
        toast.error("Error loading therapist data");
        
        // Final fallback to demo data
        setDemoTherapists();
      } finally {
        setLoading(false);
      }
    };

    const setDemoTherapists = () => {
      setTherapists([
        { email: "therapist1@example.com" },
        { email: "therapist2@example.com" },
        { email: "therapist3@example.com" }
      ]);
      setLoading(false);
    };

    loadTherapists();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8F0F8] to-white p-4 flex items-center justify-center">
        <p className="text-[#3BAFDA] font-medium">Loading therapist list...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F0F8] to-white p-4">
      <div className="container mx-auto max-w-3xl">
        <Card className="shadow-lg backdrop-blur-sm border border-[#CCCCCC] bg-white">
          <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
            <div className="flex items-center space-x-3">
              <Users className="h-8 w-8 text-[#3BAFDA]" />
              <div>
                <CardTitle className="text-2xl bg-clip-text text-transparent bg-gradient-to-r from-[#333B44] to-[#3BAFDA]">
                  User Profiles List
                </CardTitle>
                <CardDescription>Viewing all available user profiles</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {therapists.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground">No user profiles are currently available.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {therapists.map((therapist, index) => (
                  <li key={index} className="py-3 flex items-center">
                    <div className="h-8 w-8 rounded-full bg-[#E8F0F8] flex items-center justify-center mr-3">
                      <span className="text-[#3BAFDA] text-sm font-medium">{index + 1}</span>
                    </div>
                    <span className="text-[#333B44]">{therapist.email}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Test;
